/**
 * Operator settings, replacing `%LOCALAPPDATA%\CitariaApp\agent.json`.
 *
 * Two fields the agent had are gone rather than moved. `printer` went with the
 * print helper — the browser's own dialog chooses the destination now — and the
 * two binary path overrides went with the binaries. Two are new: the signed
 * print inset (see core/print.ts) and `videoEnabled`, because encoding the
 * animation is done in this tab and an operator on a slow PC may want it off.
 *
 * The Raia password is held here, in IndexedDB, rather than in a file only the
 * Windows user could read. That is a real reduction and worth being deliberate
 * about: it is the operator's own shared account, entered once per event PC and
 * never in source (§7), and the origin is the console's own. It is not stronger
 * than the agent's file, and an event PC that other people use should be signed
 * out at the end of the night — which is what `clearCredentials` is for.
 */

import { DEFAULT_INSET_IN } from '@/core/print'
import { KV, get, put } from './db'

const KEY = 'settings'

export interface Settings {
  /** Display name of the watched folder. The handle itself lives in `fs.ts`. */
  monitoredFolderName: string
  /** Display name of the folder finished sessions are filed in. Optional. */
  targetFolderName: string

  /** Flips Portrait/Landscape before the inset is chosen. Ported verbatim. */
  invertOrientation: boolean
  /** Render the archive copy before the QR is drawn. Off = parity with the WPF app. */
  archiveWithoutQr: boolean

  /** Signed print inset in inches, in Portrait terms. Negative bleeds. */
  printInsetX: number
  printInsetY: number

  /** raia-api. The desktop app hardcoded a shared account in source. */
  apiBaseUrl: string
  apiEmail: string
  apiPassword: string

  /** Encode animate.mp4 for each session. Optional; the guest still gets photos. */
  videoEnabled: boolean

  /**
   * Gallery tile size in CSS pixels — the zoom control on step 1.
   *
   * Persisted rather than per-session: an operator picks a density that suits
   * their monitor and the way they work (a few big tiles to check faces, or
   * many small ones to scan a long shoot) and should not have to set it again
   * after every restart.
   */
  galleryTileSize: number
}

/** Bounds for the gallery zoom, in CSS pixels. */
export const GALLERY_TILE_MIN = 110
export const GALLERY_TILE_MAX = 360
export const GALLERY_TILE_STEP = 50

/**
 * The backend, and the shared Raia account every event PC signs in as.
 *
 * Fixed rather than typed in. There is one account for the whole operation, so
 * asking each operator to enter it was friction that bought nothing and a
 * typo away from a console that silently never uploads.
 *
 * Note what this does and does not walk back. §7 exists because the desktop app
 * hardcoded this address AND its password in `RaiaApiClient`, and committed
 * `client_secret.json`. The password is still entered by the operator and still
 * lives only in this browser's IndexedDB — that is the half that guards the
 * account. The address is an identifier, and it ships in a bundle served
 * publicly from citaria.raiaphoto.tech, so treat it as public: it is one side
 * of the pair, and the other side must never join it here.
 */
export const RAIA_API_BASE_URL = 'https://api.raiaphoto.tech'
export const RAIA_ACCOUNT_EMAIL = 'raiaphototech@gmail.com'

export const DEFAULT_SETTINGS: Settings = {
  monitoredFolderName: '',
  targetFolderName: '',
  invertOrientation: false,
  archiveWithoutQr: false,
  printInsetX: DEFAULT_INSET_IN.Portrait.x,
  printInsetY: DEFAULT_INSET_IN.Portrait.y,
  apiBaseUrl: RAIA_API_BASE_URL,
  apiEmail: RAIA_ACCOUNT_EMAIL,
  apiPassword: '',
  videoEnabled: true,
  galleryTileSize: 150,
}

export async function loadSettings(): Promise<Settings> {
  const stored = await get<Partial<Settings>>(KV, KEY)

  const settings: Settings = {
    // Spread over the defaults rather than trusting the stored shape: a build
    // that adds a field must not leave an event PC with `undefined` in it.
    ...DEFAULT_SETTINGS,
    ...(stored ?? {}),
    // …then force the two fixed fields back over the top. Settings shows them
    // read-only, and a machine still holding the address an earlier build let
    // an operator type would otherwise display one thing and upload as another.
    apiBaseUrl: RAIA_API_BASE_URL,
    apiEmail: RAIA_ACCOUNT_EMAIL,
  }

  // Persist the correction rather than only masking it. Without this the row in
  // IndexedDB keeps the old address indefinitely — harmless, because every read
  // goes through here, but it leaves storage disagreeing with the screen and
  // with what actually uploads, which is a trap for whoever debugs this next.
  // Guarded so a normal load stays a pure read.
  if (
    stored &&
    (stored.apiBaseUrl !== RAIA_API_BASE_URL || stored.apiEmail !== RAIA_ACCOUNT_EMAIL)
  ) {
    await put(KV, settings, KEY)
  }

  return settings
}

export async function saveSettings(patch: Partial<Settings>): Promise<Settings> {
  const next = { ...(await loadSettings()), ...patch }
  await put(KV, next, KEY)
  return next
}

/**
 * Signs this machine out.
 *
 * Only the password: the account address is fixed and `loadSettings` would put
 * it straight back, and it is not the secret. Clearing the password is what
 * actually stops this browser from uploading.
 */
export async function clearCredentials(): Promise<Settings> {
  return saveSettings({ apiPassword: '' })
}

/**
 * Whether sessions can be uploaded at all. Not a block — see AppShell.
 *
 * Effectively "is there a password", now that the base URL and the account are
 * fixed. Both are still checked so this stays honest if either ever becomes
 * editable again.
 */
export function raiaConfigured(settings: Settings | null): boolean {
  return Boolean(settings?.apiBaseUrl && settings.apiEmail && settings.apiPassword)
}
